import PermissionButton from '../../permissions/PermissionButton';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Descriptions,
  message,
  Popconfirm,
  Drawer,
  Input,
  Select,
  Divider,
  Spin,
  Modal,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  RollbackOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import AddItemToDemarqueForm from './AddItemToDemarqueForm';
import NestedAchatTable from '../achat/NestedAchatTable';
import ExcelDetailImport from '../shared/ExcelDetailImport';
import {
  useFetchDemarque,
  useUpdateDemarque,
  useDeleteDemarque,
  useValidateDemarque,
  useUnvalidateDemarque,
} from '../../hooks/avoirHooks';
import { useFetchAllEntrepots } from '../../hooks/entrepotHooks';
import EditableDateField from '../../utils/EditableDateField';

const statusTag = (validated) => (
  validated ? <Tag color="green">Validée</Tag> : <Tag color="orange">Enregistrée</Tag>
);

const normalizeDetails = (details) => (
  (details || []).map((detail) => ({
    CODE_ART: detail.CODE_ART,
    ARTICLE: detail.ARTICLE || detail.article?.ARTICLE || '',
    QTE: Number(detail.QTE) || 0,
    REMARQUE: detail.REMARQUE || '',
  }))
);

const SingleDemarque = ({ refDem, onBack, onDeleted }) => {
  const { data: initialDemarque, loading, error, refetch } = useFetchDemarque(refDem);
  const { handleUpdate, loading: updating } = useUpdateDemarque();
  const { handleDelete, loading: deleting } = useDeleteDemarque();
  const { validate, loading: validating } = useValidateDemarque();
  const { unvalidate, loading: unvalidating } = useUnvalidateDemarque();
  const { entrepots, loading: loadingEntrepots } = useFetchAllEntrepots();

  const [localDemarque, setLocalDemarque] = useState(null);
  const [localItems, setLocalItems] = useState(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [isAddItemDrawerVisible, setIsAddItemDrawerVisible] = useState(false);
  const [detailSearch, setDetailSearch] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);

  const isValidated = Boolean(localDemarque?.VALIDER);

  useEffect(() => {
    if (initialDemarque) {
      setLocalDemarque({ ...initialDemarque });
      setLocalItems(normalizeDetails(initialDemarque.details || []));
    }
  }, [initialDemarque]);

  const entrepotOptions = useMemo(
    () => (entrepots || []).map((ent) => ({ value: ent.code_ent, label: `${ent.code_ent} - ${ent.name}` })),
    [entrepots]
  );

  const handleFieldChange = (field, value) => {
    setLocalDemarque((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasLocalChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!refDem) return;
    try {
      await handleUpdate(refDem, {
        CODE_ENT: localDemarque.CODE_ENT,
        REMARQUE: localDemarque.REMARQUE || undefined,
        DATE_DEM: localDemarque.DATE_DEM || undefined,
        details: localItems.map((item) => ({
          CODE_ART: item.CODE_ART,
          ARTICLE: item.ARTICLE,
          QTE: Number(item.QTE),
          REMARQUE: item.REMARQUE || undefined,
        })),
      });
      setHasLocalChanges(false);
      message.success('Démarque enregistrée');
      await refetch();
    } catch (e) {
      message.error(e?.message || "Erreur lors de l'enregistrement");
    }
  };

  const handleSave = (row) => {
    setLocalItems((prevItems) =>
      prevItems.map((item) =>
        item.CODE_ART === row.CODE_ART ? { ...item, ...row } : item
      )
    );
    setHasLocalChanges(true);
  };

  const handleDeleteItem = (codeArt) => {
    setLocalItems((prevItems) => prevItems.filter((item) => item.CODE_ART !== codeArt));
    setHasLocalChanges(true);
    message.success("Article supprimé localement. Cliquez sur 'Enregistrer' pour sauvegarder.");
  };

  const handleExcelImport = (importedItems) => {
    let addedCount = 0;
    let updatedCount = 0;
    setLocalItems((prevItems) => {
      const updated = [...(prevItems || [])];
      importedItems.forEach((newItem) => {
        const existingIndex = updated.findIndex((item) => item.CODE_ART === newItem.CODE_ART);
        if (existingIndex >= 0) {
          updated[existingIndex] = {
            ...updated[existingIndex],
            QTE: newItem.QTE,
            REMARQUE: newItem.REMARQUE || updated[existingIndex].REMARQUE,
          };
          updatedCount += 1;
        } else {
          updated.push(newItem);
          addedCount += 1;
        }
      });
      return updated;
    });
    setHasLocalChanges(true);
    message.success(
      `${addedCount} article(s) ajouté(s), ${updatedCount} mis à jour. Cliquez sur 'Enregistrer Modif.' pour sauvegarder.`
    );
  };

  const handleSelectArticle = (article) => {
    if (!article) return;
    setSelectedArticle(article);
  };

  const handleAddItem = () => {
    if (!selectedArticle) {
      message.error('Sélectionnez un article');
      return;
    }
    const existingIndex = localItems.findIndex((item) => item.CODE_ART === selectedArticle.code_art);
    if (existingIndex >= 0) {
      message.warning('Cet article est déjà dans la démarque');
      return;
    }
    const newItem = {
      CODE_ART: selectedArticle.code_art,
      ARTICLE: selectedArticle.ARTICLE,
      QTE: 1,
      REMARQUE: '',
    };
    setLocalItems((prev) => [...prev, newItem]);
    setHasLocalChanges(true);
    setSelectedArticle(null);
    setDetailSearch('');
    message.success('Article ajouté');
  };

  const handleDeleteDemarque = async () => {
    if (!refDem) return;
    try {
      await handleDelete(refDem);
      message.success('Démarque supprimée');
      if (onDeleted) {
        onDeleted();
      } else if (onBack) {
        onBack();
      }
    } catch (e) {
      message.error(e?.message || 'Erreur lors de la suppression');
    }
  };

  const handleValidate = async () => {
    if (hasLocalChanges) {
      message.warning('Enregistrez d\'abord les modifications avant de valider');
      return;
    }
    try {
      await validate(refDem);
      message.success('Démarque validée');
      if (onBack) {
        onBack();
      }
    } catch (e) {
      message.error(e?.message || 'Erreur lors de la validation');
    }
  };

  const handleUnvalidate = async () => {
    try {
      await unvalidate(refDem);
      message.success('Démarque dévalidée');
      await refetch();
    } catch (e) {
      message.error(e?.message || 'Erreur lors de la dévalidation');
    }
  };

  const totalQty = useMemo(
    () => (localItems || []).reduce((acc, item) => acc + (Number(item.QTE) || 0), 0),
    [localItems]
  );

  if (error) {
    return (
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Retour</Button>
          <span style={{ color: 'red' }}>{error}</span>
        </Space>
      </Card>
    );
  }

  return (
    <Spin spinning={loading}>
      <Card
        title={(
          <>
            Demarque - {localDemarque?.REF_DEM || ''}
            {isValidated ? (
              <Tag color="green" style={{ marginLeft: "8px" }}>
                Validée
              </Tag>
            ) : (
              <Tag color="orange" style={{ marginLeft: "8px" }}>
                Enregistrée
              </Tag>
            )}
          </>
        )}
        bordered={false}
        extra={(
          <Space>
            {hasLocalChanges && (
              <PermissionButton
                icon={<UndoOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Annuler les modifications',
                    content: 'Êtes-vous sûr de vouloir annuler toutes les modifications?',
                    okText: 'Oui, Annuler tout',
                    cancelText: 'Non, Garder les modifications',
                    onOk: () => {
                      setLocalDemarque({ ...initialDemarque });
                      setLocalItems(normalizeDetails(initialDemarque?.details || []));
                      setHasLocalChanges(false);
                      message.success('Toutes les modifications ont été annulées');
                    }
                  });
                }}
              >
                Annuler modif.
              </PermissionButton>
            )}
            {!isValidated && (
              <PermissionButton permissionModule="demarque" permissionAction="validate"
                type="primary"
                onClick={handleValidate}
                disabled={hasLocalChanges}
                loading={validating}
              >
                Valider
              </PermissionButton>
            )}
            {isValidated && (
              <PermissionButton permissionModule="demarque" permissionAction="unvalidate"
                icon={<RollbackOutlined />}
                onClick={handleUnvalidate}
                loading={unvalidating}
              >
                Dévalider
              </PermissionButton>
            )}
            <PermissionButton permissionModule="demarque" permissionAction="update"
              type="primary"
              onClick={handleSaveChanges}
              disabled={!hasLocalChanges}
              loading={updating}
            >
              Enregistrer Modif.
            </PermissionButton>
            <Popconfirm
              title="Supprimer cette démarque ?"
              onConfirm={handleDeleteDemarque}
            >
              <PermissionButton permissionModule="demarque" permissionAction="delete" danger icon={<DeleteOutlined />} loading={deleting}>
                Supprimer
              </PermissionButton>
            </Popconfirm>
            <Button
              type="default"
              onClick={onBack}
              style={{
                backgroundColor: 'white',
                color: '#EE2625',
                border: '1px solid #EE2625',
              }}
            >
              SORTIR
            </Button>
          </Space>
        )}
        style={{ position: 'relative' }}
      >
        <Descriptions
          bordered
          column={{
            xs: 1,
            sm: 2,
            md: 3,
          }}
          size="small"
          labelStyle={{ fontWeight: 'bold', textAlign: 'right' }}
          contentStyle={{ minWidth: '150px' }}
        >
          <Descriptions.Item label="Référence">{localDemarque?.REF_DEM}</Descriptions.Item>
          <Descriptions.Item label="Entrepôt">
            {isValidated ? (
              localDemarque?.entrepot?.name
                ? `${localDemarque.entrepot.code_ent} - ${localDemarque.entrepot.name}`
                : localDemarque?.CODE_ENT
            ) : (
              <Select
                showSearch
                placeholder="Entrepôt"
                options={entrepotOptions}
                value={localDemarque?.CODE_ENT}
                loading={loadingEntrepots}
                onChange={(value) => handleFieldChange('CODE_ENT', value)}
                style={{ width: '100%' }}
                disabled={isValidated}
              />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Date">
            <EditableDateField
              value={localDemarque?.DATE_DEM}
              onChange={(newDate) => handleFieldChange('DATE_DEM', newDate)}
              disabled={isValidated}
              withTime
            />
          </Descriptions.Item>
          <Descriptions.Item label="Remarque" span={2}>
            {isValidated ? (
              localDemarque?.REMARQUE || '—'
            ) : (
              <Input.TextArea
                value={localDemarque?.REMARQUE || ''}
                onChange={(e) => handleFieldChange('REMARQUE', e.target.value)}
                autoSize={{ minRows: 1, maxRows: 4 }}
              />
            )}
          </Descriptions.Item>
          {localDemarque?.DATEVALID && (
            <Descriptions.Item label="Validée le" span={2}>
              {dayjs(localDemarque.DATEVALID).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card 
        title={`Articles dans la démarque (${localItems?.length || 0}) — Qté totale: ${totalQty}`} 
        style={{ marginTop: 20 }} 
        extra={(
          <Space>
            <ExcelDetailImport
              type="demarque"
              onImport={handleExcelImport}
              disabled={isValidated}
            />
            <PermissionButton permissionModule="demarque" permissionAction="update"
              onClick={() => setIsAddItemDrawerVisible(true)}
              type="primary"
              disabled={isValidated}
            >
              Ajouter Article
            </PermissionButton>
          </Space>
        )}
      >
        <NestedAchatTable
          items={localItems || []}
          handleSave={handleSave}
          handleDelete={handleDeleteItem}
          validated={isValidated}
          type="demarque"
          showStock={false}
        />
      </Card>

      <Drawer
        title="Ajouter un article à la démarque"
        width={900}
        onClose={() => setIsAddItemDrawerVisible(false)}
        open={isAddItemDrawerVisible}
      >
        <AddItemToDemarqueForm
          refDemarque={localDemarque?.REF_DEM}
          onSuccess={(newItems) => {
            // Handle batch items from universal form
            const itemsToAdd = Array.isArray(newItems) ? newItems : [newItems];
            itemsToAdd.forEach((newItem) => {
              const existingIndex = localItems.findIndex((item) => item.CODE_ART === newItem.CODE_ART);
              if (existingIndex >= 0) {
                message.warning(`L'article ${newItem.CODE_ART} est déjà dans la démarque`);
                return;
              }
              const itemToAdd = {
                CODE_ART: newItem.CODE_ART,
                ARTICLE: newItem.ARTICLE,
                QTE: newItem.QTE || 1,
                REMARQUE: newItem.REMARQUE || '',
              };
              setLocalItems((prev) => [...prev, itemToAdd]);
            });
            setHasLocalChanges(true);
            setIsAddItemDrawerVisible(false);
            message.success(itemsToAdd.length > 1 ? `${itemsToAdd.length} articles ajoutés` : 'Article ajouté');
          }}
        />
      </Drawer>
    </Spin>
  );
};

export default SingleDemarque;
